import { Avatar, Box, Button, Chip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from 'react-i18next';
import type { Client, ProgramType, User } from '@altyor-kronos/shared-types';
import type { TeamStepValues, LinksStepValues } from '../validators';
import { TEAM_SECTIONS } from './teamSections';
import { colors } from '../../../core/theme/theme';

interface StepReviewProps {
  code: string;
  name: string;
  client: Client | null;
  program: ProgramType | null;
  team: TeamStepValues;
  links: LinksStepValues;
  photo: File | null;
  users: Array<{ id: string; data: User }>;
  onEditStep: (step: number) => void;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function SectionHeader({ label, step, onEdit }: { label: string; step: number; onEdit: (s: number) => void }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />} onClick={() => onEdit(step)}>
        {t('common.edit')}
      </Button>
    </Box>
  );
}

export default function StepReview({
  code, name, client, program, team, links, photo, users, onEditStep,
}: StepReviewProps) {
  const { t } = useTranslation();

  const userMap = new Map<string, User>();
  for (const u of users) userMap.set(u.id, u.data);

  const nonEmptySections = TEAM_SECTIONS.filter((s) => (team[s.key]?.length ?? 0) > 0);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        {t('projects.wizard.reviewTitle', 'Review your project')}
      </Typography>

      {/* Code + Name */}
      <SectionHeader label={t('projects.wizard.stepCode', 'Code & Name')} step={0} onEdit={onEditStep} />
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6">{code} — {name}</Typography>
      </Box>

      {/* Client */}
      <SectionHeader label={t('projects.wizard.stepClient', 'Client')} step={1} onEdit={onEditStep} />
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
        {client ? (
          <>
            <Avatar src={client.logoURL ?? undefined} variant="square" sx={{ width: 48, height: 48 }}>
              {client.name[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{client.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[client.industry, client.country].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </>
        ) : (
          <Typography color="text.secondary">{t('projects.wizard.reviewNoClient')}</Typography>
        )}
      </Box>

      {/* Program */}
      <SectionHeader label={t('projects.wizard.stepProgram', 'Program')} step={2} onEdit={onEditStep} />
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        {program ? (
          <Chip label={program} sx={{ bgcolor: colors.yellow, color: '#000', fontWeight: 600 }} />
        ) : (
          <Typography color="text.secondary">{t('projects.wizard.reviewNoProgram')}</Typography>
        )}
      </Box>

      {/* Team */}
      <SectionHeader label={t('projects.wizard.stepTeam', 'Team')} step={3} onEdit={onEditStep} />
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        {nonEmptySections.length === 0 ? (
          <Typography color="text.secondary">{t('projects.wizard.reviewNoTeam')}</Typography>
        ) : (
          nonEmptySections.map((section) => (
            <Box key={section.key} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t(section.labelKey, section.key)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {team[section.key].map((uid) => {
                  const u = userMap.get(uid);
                  if (!u) return null;
                  return (
                    <Chip
                      key={uid}
                      avatar={
                        <Avatar src={u.photoURL ?? undefined} variant="square" sx={{ width: 24, height: 24 }}>
                          {initials(u.displayName)[0]}
                        </Avatar>
                      }
                      label={u.displayName}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Links */}
      <SectionHeader label={t('projects.wizard.stepLinks', 'Links & Media')} step={4} onEdit={onEditStep} />
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
        {links.sharepointLink && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>{t('projects.wizard.reviewSharepoint')} {links.sharepointLink}</Typography>
        )}
        {links.teamsLink && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>{t('projects.wizard.reviewTeams')} {links.teamsLink}</Typography>
        )}
        {links.customerWebsite && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>{t('projects.wizard.reviewWebsite')} {links.customerWebsite}</Typography>
        )}
        {!links.sharepointLink && !links.teamsLink && !links.customerWebsite && !photo && (
          <Typography color="text.secondary">{t('projects.wizard.reviewNoLinks')}</Typography>
        )}
        {photo && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={URL.createObjectURL(photo)}
              sx={{ width: 48, height: 48, objectFit: 'cover' }}
            />
            <Typography variant="body2">{photo.name}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
